import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Loader from "src/components/common/loader/loader";
import {
  SequenceService,
  type ReviewOut,
  type ReviewTouchOut,
  type SenderIdentity,
} from "@services/sequence/sequenceService";
import TouchPreview from "../engagement/components/phases/emailSms/TouchPreview";
import type { Touch } from "../engagement/components/phases/emailSms/emailSmsTypes";
import { useAdTokens } from "@/shared/adTokens/useAdTokens";
import {
  PERSON_TOKENS,
  vocabularyOf,
} from "../engagement/components/phases/emailSms/touchCopy";
import "./sequenceReview.scss";

const service = new SequenceService();

/*
    Where a client reads every message in a playbook before any of it sends (MPN-2207).

    One playbook is one request, so this page is scoped to one and its actions read as
    playbook-level: the bar pinned across the bottom holds Approve All Remaining and Submit
    Review, not the per-touch buttons, because approving a single message is not a decision
    anybody wants to make twenty times without seeing where they are.

    Each touch is marked Good to go or Needs a change, and a change must carry a comment —
    a flag with no reason is a round trip nobody can act on.
*/
export default function SequenceReviewPage() {
  const { launchId, sequenceId } = useParams<{ launchId: string; sequenceId: string }>();
  const navigate = useNavigate();
  const lid = Number(launchId);
  const sid = Number(sequenceId);

  const [review, setReview] = useState<ReviewOut | null>(null);
  const [selected, setSelected] = useState(0);
  const [frame, setFrame] = useState<"desktop" | "mobile">("desktop");
  const [comment, setComment] = useState("");
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmApproveAll, setConfirmApproveAll] = useState(false);
  const [tokenValues, setTokenValues] = useState<Record<string, string>>({});

  /*
      The client sees this campaign's real values, not token names — the whole point of
      reviewing a rendered message.

      Only this campaign's values. The per-student fields used to be filled from a constant
      in the UI holding `first_name: "Alex"`, so every client approving a playbook read
      somebody else's sample name as though it were their own data. `[FIRST NAME]` has no
      campaign-wide value and never will; the preview marks it amber and the note under it
      says it fills in per student, which is the honest answer.
  */
  const previewValues = tokenValues;

  /* Known-but-unvalued has to read as "no value", not as a token the engine does not know. */
  const { tokens: adTokens } = useAdTokens();
  const vocabulary = useMemo(
    () => vocabularyOf([...adTokens, ...PERSON_TOKENS]),
    [adTokens],
  );

  /*
      This campaign's own sender, resolved server-side (MPN-2201).

      It was a constant, so every client reviewing a playbook was shown the same From address
      -- and the brand name beside it was theirs, which made the pairing read as an address
      belonging to their school. The client is approving a message; the From line has to be
      the one the message will actually carry.
  */
  const [sender, setSender] = useState<SenderIdentity>({
    from_name: "", from_address: "", sms_sender: "", footer_address: "",
  });

  useEffect(() => {
    if (!lid) return;
    let cancelled = false;
    service.getSenderIdentity(lid).then((identity) => {
      if (!cancelled) setSender(identity);
    }).catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [lid]);

  const load = useCallback(async () => {
    if (!lid || !sid) return;
    try {
      setReview(await service.getReview(lid, sid));
    } catch {
      setError("Could not load this review.");
    }
  }, [lid, sid]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!lid) return;
    service
      .getTokenValues(lid)
      .then((values) => setTokenValues(values ?? {}))
      .catch(() => undefined);
  }, [lid]);

  const touches = review?.touches ?? [];
  const current: ReviewTouchOut | undefined = touches[selected];

  // The preview is the Ops one, unchanged: the client must see what Ops saw.
  const asTouch = useMemo<Touch | null>(
    () =>
      current
        ? ({
            id: String(current.ad_id),
            ch: current.channel as Touch["ch"],
            title: current.title,
            days: 0,
            dir: "on",
            anchor: "",
            hr: 10,
            grp: "main",
            on: true,
            pub: true,
            ver: 1,
            subject: current.subject,
            pre: current.preheader,
            body: current.html,
            text: current.text,
          } as Touch)
        : null,
    [current],
  );

  useEffect(() => {
    setComment(current?.comment ?? "");
  }, [current?.ad_id, current?.comment]);

  const decide = async (approved: boolean) => {
    if (!current) return;
    if (!approved && !comment.trim()) {
      setError("Tell us what needs changing — the team cannot act on a flag with no note.");
      return;
    }
    setIsBusy(true);
    setError(null);
    try {
      setReview(await service.decideTouch(lid, sid, current.ad_id, approved, comment.trim()));
      // Straight on to the next unreviewed message, so a long playbook keeps moving.
      const next = touches.findIndex((t, i) => i > selected && !t.verdict);
      if (next > -1) setSelected(next);
    } catch {
      setError("Could not save that. Try again.");
    } finally {
      setIsBusy(false);
    }
  };

  const approveRemaining = async (alsoSubmit: boolean) => {
    setIsBusy(true);
    setConfirmApproveAll(false);
    try {
      let next = await service.approveRemaining(lid, sid);
      if (alsoSubmit && next.progress.can_submit) next = await service.submitReview(lid, sid);
      setReview(next);
    } catch {
      setError("Could not approve the remaining messages.");
    } finally {
      setIsBusy(false);
    }
  };

  const submit = async () => {
    setIsBusy(true);
    try {
      setReview(await service.submitReview(lid, sid));
    } catch {
      setError("Could not submit the review.");
    } finally {
      setIsBusy(false);
    }
  };

  if (!review) return error ? <div className="alert alert-danger m-3">{error}</div> : <Loader />;

  const p = review.progress;
  const unreviewed = p.total - p.reviewed;

  return (
    <div className="sqr">
      <header className="sqr__head">
        <button type="button" className="sqr__back" onClick={() => navigate(-1)}>
          ‹ Back
        </button>
        <h5>{review.sequence_name}</h5>
        <span className={`sqr__state sqr__state--${review.state}`}>
          {review.state === "changes"
            ? "Changes Requested"
            : review.state === "approved"
              ? "Approved"
              : review.state === "awaiting"
                ? "In Review"
                : "Not submitted"}
        </span>
      </header>

      <div className="sqr__body">
        <nav className="sqr__list" aria-label="Messages in this sequence">
          <div className="sqr__list-head">
            {p.reviewed} of {p.total} reviewed
          </div>
          {touches.map((t, i) => (
            <button
              type="button"
              key={t.ad_id}
              className={`sqr__item${i === selected ? " sqr__item--on" : ""}`}
              onClick={() => setSelected(i)}
            >
              <span className="sqr__num">{i + 1}</span>
              <span className={`esms-chan esms-chan--${t.channel}`}>
                {t.channel === "sms" ? "SMS" : "EMAIL"}
              </span>
              <span className="sqr__item-title">{t.title}</span>
              <span className={`sqr__verdict sqr__verdict--${t.verdict || "none"}`}>
                {t.verdict === "good"
                  ? "Good to go"
                  : t.verdict === "change"
                    ? "Needs a change"
                    : "Not reviewed"}
              </span>
            </button>
          ))}
        </nav>

        <main className="sqr__stage">
          {current && (
            <>
              <div className="sqr__stage-head">
                <span className="sqr__stage-count">
                  Touch {selected + 1} of {touches.length}
                </span>
                <span className="sqr__stage-title">{current.title}</span>
                {current.channel === "email" && (
                  <div className="sqr__frames" role="group" aria-label="Preview width">
                    {(["desktop", "mobile"] as const).map((f) => (
                      <button
                        type="button"
                        key={f}
                        aria-pressed={frame === f}
                        className={`sqr__frame${frame === f ? " sqr__frame--on" : ""}`}
                        onClick={() => setFrame(f)}
                      >
                        {f === "desktop" ? "Desktop" : "Mobile"}
                      </button>
                    ))}
                  </div>
                )}
                <div className="sqr__step">
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    disabled={selected === 0}
                    onClick={() => setSelected((i) => i - 1)}
                  >
                    ‹ Previous
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    disabled={selected >= touches.length - 1}
                    onClick={() => setSelected((i) => i + 1)}
                  >
                    Next ›
                  </button>
                </div>
              </div>

              {asTouch && (
                <TouchPreview
                  touch={asTouch}
                  values={previewValues}
              vocabulary={vocabulary}
                  frame={frame}
                  fromName={sender.from_name}
                  fromAddress={sender.from_address}
                  smsSender={sender.sms_sender}
                  footerAddress={sender.footer_address}
                />
              )}

              <div className="sqr__decide">
                <label htmlFor="sqr-comment">
                  Comment {current.verdict === "change" && <span>· required to flag</span>}
                </label>
                <textarea
                  id="sqr-comment"
                  value={comment}
                  placeholder="What should change about this message?"
                  onChange={(e) => setComment(e.target.value)}
                />
                <div className="sqr__decide-btns">
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    disabled={isBusy}
                    onClick={() => decide(false)}
                  >
                    Needs a change
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={isBusy}
                    onClick={() => decide(true)}
                  >
                    Good to go
                  </button>
                </div>
                {error && <p className="sqr__err">{error}</p>}
              </div>
            </>
          )}
        </main>

        <aside className="sqr__comments" aria-label="Review comments">
          <div className="sqr__comments-head">Review Comments</div>
          {touches.filter((t) => t.comment).length === 0 ? (
            <p className="sqr__comments-empty">No review comments yet</p>
          ) : (
            touches
              .filter((t) => t.comment)
              .map((t) => (
                <div className="sqr__comment" key={t.ad_id}>
                  <div className="sqr__comment-on">{t.title}</div>
                  <p>“{t.comment}”</p>
                </div>
              ))
          )}
        </aside>
      </div>

      {/* Playbook-level, pinned, so the actions never read as being about one message. */}
      <footer className="sqr__bar">
        <span className="sqr__bar-sum">
          {p.reviewed} of {p.total} reviewed · {p.good} good to go · {p.needs_change} need a
          change
        </span>
        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          disabled={isBusy || unreviewed === 0}
          onClick={() => setConfirmApproveAll(true)}
        >
          Approve All Remaining ({unreviewed})
        </button>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={isBusy || !p.can_submit}
          onClick={submit}
        >
          Submit Review for {review.sequence_name}
        </button>
      </footer>

      {confirmApproveAll && (
        <div
          className="esms-modal__scrim"
          role="dialog"
          aria-modal="true"
          aria-label="Approve all remaining"
          onClick={() => setConfirmApproveAll(false)}
        >
          <div className="esms-modal" onClick={(e) => e.stopPropagation()}>
            <div className="esms-modal__head">
              <h5>Approve All Remaining</h5>
            </div>
            <div className="esms-modal__body">
              <p>
                This marks the <strong>{unreviewed}</strong> message
                {unreviewed === 1 ? "" : "s"} you have not reviewed as Good to go.
              </p>
              {p.needs_change > 0 && (
                <p className="esms-modal__note">
                  The {p.needs_change} message{p.needs_change === 1 ? "" : "s"} you marked
                  Needs a change {p.needs_change === 1 ? "stays" : "stay"} flagged.
                </p>
              )}
            </div>
            <div className="esms-modal__foot">
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={() => approveRemaining(false)}
              >
                Approve only
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => approveRemaining(true)}
              >
                Approve and Submit Review
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
